import { useContext, useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import Common from '../../../shared/Common';
import type { IUserSettingsChildProps } from '../UserSettings';
import type { IUserPreference } from '../../../../interfaces/IUserPreference';
import { DictionaryContext } from '../../../../App';

const Preferences = (props: IUserSettingsChildProps) => {
  const [userPreferencesChanged, setUserPreferencesChanged] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [userPreferencesToSave, setUserPreferencesToSave] = useState<IUserPreference>({
    recurringOperationDashboardStatsCount: 0,
    showWeeklyExpensesDashboardStats: false,
    showMonthlyExpensesDashboardStats: false,
    showYearlyExpensesDashboardStats: false,
    showSummaryTotalDashboardStats: false,
    showSummaryLocationsDashboardStats: false,
    monthlyTargetBalance: 0,
    showVatModule: false,
    predictionStartingAmount: 0,
    predictionStartDate: '',
    predictionEndDate: '',
    predictionTargetBalance: 0,
    interestCapitalization: 12,
    showAveragePredictionTime: true,
    showAverage3Months: false,
    showAverage6Months: false,
    showAverage12Months: false,
    interestRate: 0,
  });
  const { loading, userData, setStatus, setFetchUserDataTrigger } = props;
  const { reloadUserPreferences } = useContext(DictionaryContext);

  useEffect(() => {
    if (userData?.userPreference) {
      setUserPreferencesToSave(userData?.userPreference);
    }
  }, [userData?.userPreference]);

  const updateUserPreferences = async () => {
    setDataLoading(true);
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userPreference: userPreferencesToSave }),
    };
    const response = await Common.authorizedFetch('api/users/updateUserPreference', requestOptions);
    const data = await response.json();
    setDataLoading(false);
    if (!data.success) {
      alert(data.errors);
    } else {
      setStatus({
        variant: 'success',
        status: 'Ustawienia zostały zaktualizowane.',
        messageTime: Date.now(),
      });
      if (reloadUserPreferences) {
        reloadUserPreferences();
      }
      setFetchUserDataTrigger((prev) => prev + 1);
    }
  };

  return (
    <>
      <h4>Preferencje użytkownika</h4>
      <h6 className='mt-3'>Wyświetlanie modułów</h6>
      <Row className='mt-2'>
        <Col xs={6} md={4}>
          {loading || dataLoading ? (
            <Skeleton />
          ) : (
            <Form.Check
              type='switch'
              id='showVatModule-switch'
              style={{ userSelect: 'none' }}
              label='Pokaż moduł VAT'
              checked={userPreferencesToSave.showVatModule}
              onChange={() => {
                setUserPreferencesChanged(true);
                const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                updatedUserPreferencesToSave.showVatModule = !updatedUserPreferencesToSave.showVatModule;
                setUserPreferencesToSave(updatedUserPreferencesToSave);
              }}
            />
          )}
        </Col>
      </Row>
      <h6 className='mt-3'>Wyświetlanie statystyk wydatków</h6>
      <Row className='mt-2'>
        <Col xs={6} md={4}>
          {loading || dataLoading ? (
            <Skeleton />
          ) : (
            <Form.Check
              type='switch'
              id='showWeeklyExpensesDashboardStats-switch'
              style={{ userSelect: 'none' }}
              label='Pokaż tygodniowe wydatki'
              checked={userPreferencesToSave.showWeeklyExpensesDashboardStats}
              onChange={() => {
                setUserPreferencesChanged(true);
                const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                updatedUserPreferencesToSave.showWeeklyExpensesDashboardStats =
                  !updatedUserPreferencesToSave.showWeeklyExpensesDashboardStats;
                setUserPreferencesToSave(updatedUserPreferencesToSave);
              }}
            />
          )}
        </Col>
        <Col xs={6} md={4}>
          {loading || dataLoading ? (
            <Skeleton />
          ) : (
            <Form.Check
              type='switch'
              id='showMonthlyExpensesDashboardStats-switch'
              style={{ userSelect: 'none' }}
              label='Pokaż miesięczne wydatki'
              checked={userPreferencesToSave.showMonthlyExpensesDashboardStats}
              onChange={() => {
                setUserPreferencesChanged(true);
                const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                updatedUserPreferencesToSave.showMonthlyExpensesDashboardStats =
                  !updatedUserPreferencesToSave.showMonthlyExpensesDashboardStats;
                setUserPreferencesToSave(updatedUserPreferencesToSave);
              }}
            />
          )}
        </Col>
        <Col xs={6} md={4}>
          {loading || dataLoading ? (
            <Skeleton />
          ) : (
            <Form.Check
              type='switch'
              id='showYearlyExpensesDashboardStats-switch'
              style={{ userSelect: 'none' }}
              label='Pokaż roczne wydatki'
              checked={userPreferencesToSave.showYearlyExpensesDashboardStats}
              onChange={() => {
                setUserPreferencesChanged(true);
                const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                updatedUserPreferencesToSave.showYearlyExpensesDashboardStats =
                  !updatedUserPreferencesToSave.showYearlyExpensesDashboardStats;
                setUserPreferencesToSave(updatedUserPreferencesToSave);
              }}
            />
          )}
        </Col>
      </Row>
      <h6 className='mt-3'>Wyświetlanie statystyk ogólnych</h6>
      <Row className='mt-2'>
        <Col xs={6} md={4}>
          {loading || dataLoading ? (
            <Skeleton />
          ) : (
            <Form.Check
              type='switch'
              id='showSummaryTotalDashboardStats-switch'
              style={{ userSelect: 'none' }}
              label='Pokaż sumaryczne statystyki'
              checked={userPreferencesToSave.showSummaryTotalDashboardStats}
              onChange={() => {
                setUserPreferencesChanged(true);
                const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                updatedUserPreferencesToSave.showSummaryTotalDashboardStats =
                  !updatedUserPreferencesToSave.showSummaryTotalDashboardStats;
                setUserPreferencesToSave(updatedUserPreferencesToSave);
              }}
            />
          )}
        </Col>
        <Col xs={6} md={4}>
          {loading || dataLoading ? (
            <Skeleton />
          ) : (
            <Form.Check
              type='switch'
              id='showSummaryLocationsDashboardStats-switch'
              style={{ userSelect: 'none' }}
              label='Pokaż najliczniejsze lokalizacje'
              checked={userPreferencesToSave.showSummaryLocationsDashboardStats}
              onChange={() => {
                setUserPreferencesChanged(true);
                const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                updatedUserPreferencesToSave.showSummaryLocationsDashboardStats =
                  !updatedUserPreferencesToSave.showSummaryLocationsDashboardStats;
                setUserPreferencesToSave(updatedUserPreferencesToSave);
              }}
            />
          )}
        </Col>
      </Row>
      <h6 className='mt-3'>Wyświetlanie najbliższych operacji cyklicznych</h6>
      <Row className='mt-2'>
        <Col sm={6}>
          {loading || dataLoading ? (
            <Skeleton height={40} />
          ) : (
            <>
              <Form.Group as={Row} className='mb-3'>
                <Row className='align-items-center'>
                  <Col xs='auto' className='pe-0'>
                    <Form.Control
                      style={{ width: '50px' }}
                      type='number'
                      value={userPreferencesToSave.recurringOperationDashboardStatsCount}
                      size='sm'
                      max={7}
                      min={0}
                      onChange={(event) => {
                        const number = Number(event.target.value);
                        if (!isNaN(number)) {
                          setUserPreferencesChanged(true);
                          const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                          updatedUserPreferencesToSave.recurringOperationDashboardStatsCount = number;
                          setUserPreferencesToSave(updatedUserPreferencesToSave);
                        }
                      }}
                    />
                  </Col>
                  <Col>
                    <Form.Label>Maksymalna liczba prezentowanych operacji cyklicznych</Form.Label>
                  </Col>
                </Row>
                <Row>
                  <Form.Range
                    className='ps-3'
                    min={0}
                    max={7}
                    value={userPreferencesToSave.recurringOperationDashboardStatsCount}
                    onChange={(event) => {
                      const number = Number(event.target.value);
                      if (!isNaN(number)) {
                        setUserPreferencesChanged(true);
                        const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                        updatedUserPreferencesToSave.recurringOperationDashboardStatsCount = number;
                        setUserPreferencesToSave(updatedUserPreferencesToSave);
                      }
                    }}
                  />
                </Row>
              </Form.Group>
            </>
          )}
        </Col>
      </Row>
      <h6>Statystyki</h6>
      <Row className='mt-2'>
        <Col>
          {loading || dataLoading ? (
            <Skeleton height={40} />
          ) : (
            <>
              <Form.Group as={Row} className='mb-3'>
                <Row className='align-items-center'>
                  <Col xs='auto' className='pe-0'>
                    <Form.Control
                      style={{ width: '100px' }}
                      type='number'
                      value={userPreferencesToSave.monthlyTargetBalance}
                      size='sm'
                      min={0}
                      onChange={(event) => {
                        const number = Number(event.target.value);
                        if (!isNaN(number)) {
                          setUserPreferencesChanged(true);
                          const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                          updatedUserPreferencesToSave.monthlyTargetBalance = number;
                          setUserPreferencesToSave(updatedUserPreferencesToSave);
                        }
                      }}
                    />
                  </Col>
                  <Col>
                    <Form.Label>Miesięczny cel bilansu dla którego liczone są statystyki (w zł)</Form.Label>
                  </Col>
                </Row>
              </Form.Group>
            </>
          )}
        </Col>
      </Row>
      <h6 className='mt-3'>Prognoza finansowa</h6>
      <Row className='mt-2'>
        <Col>
          {loading || dataLoading ? (
            <Skeleton height={40} />
          ) : (
            <>
              <Form.Group as={Row} className='mb-3'>
                <Col>
                  <Row className='align-items-center'>
                    <Col xs='auto' className='pe-0'>
                      <Form.Control
                        style={{ width: '150px' }}
                        type='month'
                        value={userPreferencesToSave.predictionStartDate?.split(',')[0].substring(0, 7) ?? ''}
                        size='sm'
                        onChange={(event) => {
                          setUserPreferencesChanged(true);
                          const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                          updatedUserPreferencesToSave.predictionStartDate = event.target.value;
                          setUserPreferencesToSave(updatedUserPreferencesToSave);
                        }}
                      />
                    </Col>
                    <Col>
                      <Form.Label>Data początkowa prognozy</Form.Label>
                    </Col>
                  </Row>
                </Col>
                <Col>
                  <Row className='align-items-center'>
                    <Col xs='auto' className='pe-0'>
                      <Form.Control
                        style={{ width: '150px' }}
                        type='month'
                        value={userPreferencesToSave.predictionEndDate?.split(',')[0].substring(0, 7) ?? ''}
                        size='sm'
                        onChange={(event) => {
                          setUserPreferencesChanged(true);
                          const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                          updatedUserPreferencesToSave.predictionEndDate = event.target.value;
                          setUserPreferencesToSave(updatedUserPreferencesToSave);
                        }}
                      />
                    </Col>
                    <Col>
                      <Form.Label>Data końca prognozy</Form.Label>
                    </Col>
                  </Row>
                </Col>
              </Form.Group>
              <Form.Group as={Row} className='mb-3'>
                <Col>
                  <Row className='align-items-center'>
                    <Col xs='auto' className='pe-0'>
                      <Form.Control
                        style={{ width: '150px' }}
                        type='number'
                        value={userPreferencesToSave.predictionStartingAmount}
                        size='sm'
                        min={0}
                        onChange={(event) => {
                          const number = Number(event.target.value);
                          if (!isNaN(number)) {
                            setUserPreferencesChanged(true);
                            const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                            updatedUserPreferencesToSave.predictionStartingAmount = number;
                            setUserPreferencesToSave(updatedUserPreferencesToSave);
                          }
                        }}
                      />
                    </Col>
                    <Col>
                      <Form.Label>Budzet początkowy (w zł)</Form.Label>
                    </Col>
                  </Row>
                </Col>
                <Col>
                  <Row className='align-items-center'>
                    <Col xs='auto' className='pe-0'>
                      <Form.Control
                        style={{ width: '150px' }}
                        type='number'
                        value={userPreferencesToSave.predictionTargetBalance}
                        size='sm'
                        min={0}
                        onChange={(event) => {
                          const number = Number(event.target.value);
                          if (!isNaN(number)) {
                            setUserPreferencesChanged(true);
                            const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                            updatedUserPreferencesToSave.predictionTargetBalance = number;
                            setUserPreferencesToSave(updatedUserPreferencesToSave);
                          }
                        }}
                      />
                    </Col>
                    <Col>
                      <Form.Label>Cel finansowy (w zł)</Form.Label>
                    </Col>
                  </Row>
                </Col>
              </Form.Group>
              <Form.Group as={Row} className='mb-3'>
                <Col>
                  <Row className='align-items-center'>
                    <Col xs='auto' className='pe-0'>
                      <Form.Control
                        style={{ width: '150px' }}
                        type='number'
                        defaultValue={userPreferencesToSave.interestRate}
                        size='sm'
                        min={0}
                        onChange={(event) => {
                          const number = Number(event.target.value);
                          if (!isNaN(number)) {
                            setUserPreferencesChanged(true);
                            const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                            updatedUserPreferencesToSave.interestRate = number;
                            setUserPreferencesToSave(updatedUserPreferencesToSave);
                          }
                        }}
                      />
                    </Col>
                    <Col>
                      <Form.Label>Stopa procentowa (%)</Form.Label>
                    </Col>
                  </Row>
                </Col>
                <Col>
                  <Row className='align-items-center'>
                    <Col xs='auto' className='pe-0'>
                      <Form.Select
                        value={userPreferencesToSave.interestCapitalization}
                        size='sm'
                        onChange={(e) => {
                          const value = Number(e.target.value) as 1 | 3 | 12;
                          if (!isNaN(value)) {
                            setUserPreferencesChanged(true);
                            const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                            updatedUserPreferencesToSave.interestCapitalization = value;
                            setUserPreferencesToSave(updatedUserPreferencesToSave);
                          }
                        }}
                        style={{
                          width: '150px',
                        }}
                      >
                        <option value={1}>Miesięczna</option>
                        <option value={3}>Kwartałowa</option>
                        <option value={12}>Roczna</option>
                      </Form.Select>
                    </Col>
                    <Col>
                      <Form.Label>Kapitalizacja odsetek</Form.Label>
                    </Col>
                  </Row>
                </Col>
              </Form.Group>
              <Form.Group as={Row} className='mb-3'>
                <Col>
                  <div>
                    <Form.Check
                      type='checkbox'
                      id='showAverage-prediction-time-switch'
                      style={{ userSelect: 'none' }}
                      label='Średnia dotychczasowa'
                      checked={userPreferencesToSave.showAveragePredictionTime}
                      onChange={() => {
                        setUserPreferencesChanged(true);
                        const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                        updatedUserPreferencesToSave.showAveragePredictionTime =
                          !updatedUserPreferencesToSave.showAveragePredictionTime;
                        setUserPreferencesToSave(updatedUserPreferencesToSave);
                      }}
                    />
                  </div>
                </Col>
                <Col>
                  <Form.Check
                    type='checkbox'
                    id='showAverage-3-months-switch'
                    style={{ userSelect: 'none' }}
                    label='Średnia z 3 miesięcy'
                    checked={userPreferencesToSave.showAverage3Months}
                    onChange={() => {
                      setUserPreferencesChanged(true);
                      const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                      updatedUserPreferencesToSave.showAverage3Months =
                        !updatedUserPreferencesToSave.showAverage3Months;
                      setUserPreferencesToSave(updatedUserPreferencesToSave);
                    }}
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className='mb-3'>
                <Col>
                  <Form.Check
                    type='checkbox'
                    id='showAverage-6-months-switch'
                    style={{ userSelect: 'none' }}
                    label='Średnia z 6 miesięcy'
                    checked={userPreferencesToSave.showAverage6Months}
                    onChange={() => {
                      setUserPreferencesChanged(true);
                      const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                      updatedUserPreferencesToSave.showAverage6Months =
                        !updatedUserPreferencesToSave.showAverage6Months;
                      setUserPreferencesToSave(updatedUserPreferencesToSave);
                    }}
                  />
                </Col>
                <Col>
                  <Form.Check
                    type='checkbox'
                    id='showAverage-12-months-switch'
                    style={{ userSelect: 'none' }}
                    label='Średnia z 12 miesięcy'
                    checked={userPreferencesToSave.showAverage12Months}
                    onChange={() => {
                      setUserPreferencesChanged(true);
                      const updatedUserPreferencesToSave = { ...userPreferencesToSave };
                      updatedUserPreferencesToSave.showAverage12Months =
                        !updatedUserPreferencesToSave.showAverage12Months;
                      setUserPreferencesToSave(updatedUserPreferencesToSave);
                    }}
                  />
                </Col>
              </Form.Group>
            </>
          )}
        </Col>
      </Row>
      <Button
        className='mt-3'
        variant='primary'
        onClick={updateUserPreferences}
        disabled={loading || dataLoading || !userPreferencesChanged}
      >
        Zapisz
      </Button>
    </>
  );
};

export default Preferences;
